import environments from './environments.json';

export type BrowserApplication = 'admin' | 'portal' | 'casaweg';
export interface BrowserConfig {
  readonly schemaVersion: 1;
  readonly environment: 'dev' | 'demo' | 'live' | 'local';
  readonly application: BrowserApplication;
  readonly apiOrigin: string;
  readonly appOrigin: string;
  readonly recaptcha: { readonly siteKey: string; readonly disabled: boolean };
}

export interface BrowserConfigContext {
  application: BrowserApplication;
  origin: string;
  allowLocal?: boolean;
}

let configuration: BrowserConfig | undefined;

function isLoopbackOrigin(value: string): boolean {
  try {
    const url = new URL(value);
    return ['http:', 'https:'].includes(url.protocol)
      && ['localhost', '127.0.0.1', '[::1]'].includes(url.hostname)
      && url.origin === value;
  } catch {
    return false;
  }
}

/** Validate a complete public document; never interpret .env files in the browser. */
export function validateBrowserConfig(value: unknown, context: BrowserConfigContext): BrowserConfig {
  if (!value || typeof value !== 'object' || Array.isArray(value)) throw new Error('Konfiguration fehlt');
  const row = value as Record<string, unknown>;
  const keys = ['schemaVersion', 'environment', 'application', 'apiOrigin', 'appOrigin', 'recaptcha'];
  if (Object.keys(row).some((key) => !keys.includes(key)) || keys.some((key) => !(key in row))) {
    throw new Error('Unbekannte Konfigurationsfelder');
  }
  if (row.schemaVersion !== 1 || row.application !== context.application || row.appOrigin !== context.origin) {
    throw new Error('Konfiguration passt nicht zur Anwendung');
  }
  if (row.environment === 'local') {
    if (!context.allowLocal || !isLoopbackOrigin(context.origin) || typeof row.apiOrigin !== 'string'
      || !isLoopbackOrigin(row.apiOrigin)) throw new Error('Lokales Ziel nicht erlaubt');
  } else {
    const profile = environments[row.environment as keyof typeof environments];
    if (!Object.prototype.hasOwnProperty.call(environments, String(row.environment)) || !profile
      || row.apiOrigin !== profile.apiOrigin || row.appOrigin !== profile.origins[context.application]) {
      throw new Error('Umgebung oder Serverziel stimmt nicht');
    }
  }
  const recaptcha = row.recaptcha as Record<string, unknown> | null;
  if (!recaptcha || typeof recaptcha !== 'object' || Array.isArray(recaptcha)
    || Object.keys(recaptcha).some((key) => !['siteKey', 'disabled'].includes(key))
    || typeof recaptcha.siteKey !== 'string' || typeof recaptcha.disabled !== 'boolean'
    || recaptcha.siteKey.length > 256 || /\s/.test(recaptcha.siteKey)) {
    throw new Error('Anmeldekonfiguration ungültig');
  }
  if (row.environment !== 'local' && (recaptcha.disabled
    || (context.application === 'admin' && !recaptcha.siteKey))) {
    throw new Error('Anmeldeschutz fehlt');
  }
  return Object.freeze({
    schemaVersion: 1,
    environment: row.environment as BrowserConfig['environment'],
    application: context.application,
    apiOrigin: row.apiOrigin as string,
    appOrigin: context.origin,
    recaptcha: Object.freeze({ siteKey: recaptcha.siteKey, disabled: recaptcha.disabled }),
  });
}

export function installBrowserConfig(value: unknown, context: BrowserConfigContext): BrowserConfig {
  configuration = validateBrowserConfig(value, context);
  return configuration;
}

export function getBrowserConfig(): BrowserConfig {
  if (!configuration) throw new Error('Anwendung vor Laufzeitkonfiguration gestartet');
  return configuration;
}

export const getApiOrigin = (): string => getBrowserConfig().apiOrigin;

export async function loadBrowserConfig(context: BrowserConfigContext): Promise<BrowserConfig> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 15000);
  try {
    const response = await fetch('/runtime-config.json', {
      cache: 'no-store', credentials: 'omit', redirect: 'error', signal: controller.signal,
    });
    if (!response.ok || !response.headers.get('content-type')?.includes('application/json')) {
      throw new Error('Konfiguration nicht verfügbar');
    }
    return installBrowserConfig(await response.json(), context);
  } finally {
    clearTimeout(timeout);
  }
}

/** App modules (and their API clients) must only be imported after configuration. */
export async function startBrowserApplication(
  application: BrowserApplication,
  importApplication: () => Promise<unknown>,
  allowLocal = false,
): Promise<void> {
  const root = document.getElementById('root');
  try {
    if (root) {
      root.textContent = 'Anwendung wird geladen …';
      root.setAttribute('role', 'status');
    }
    await loadBrowserConfig({ application, origin: window.location.origin, allowLocal });
    root?.removeAttribute('role');
    await importApplication();
  } catch {
    if (!root) return;
    root.replaceChildren();
    root.setAttribute('role', 'alert');
    const panel = document.createElement('main');
    panel.style.cssText = 'max-width:32rem;margin:12vh auto;padding:24px;font:16px/1.5 system-ui;color:#172b4d';
    const title = document.createElement('h1');
    title.textContent = 'Anwendung konnte nicht gestartet werden';
    title.style.fontSize = '1.5rem';
    const text = document.createElement('p');
    text.textContent = 'Die Verbindung konnte nicht sicher vorbereitet werden. Bitte laden Sie die Seite erneut.';
    const button = document.createElement('button');
    button.type = 'button';
    button.textContent = 'Erneut laden';
    button.style.cssText = 'min-height:44px;padding:10px 20px;font:inherit;cursor:pointer';
    button.addEventListener('click', () => window.location.reload());
    panel.append(title, text, button);
    root.append(panel);
  }
}
